/** @jsxImportSource @emotion/react */
import {
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLink,
  EuiSpacer,
} from "@inscopix/ideas-eui";
import { ToolEditorFormCode } from "./ToolEditorFormCode/ToolEditorFormCode";
import { useToolEditorContext } from "components/ToolEditor/Provider/ToolEditorProvider";
import { ToolEditorSpecEditor } from "./ToolEditorSpecEditor/ToolEditorSpecEditor";
import { ToolEditorFormTest } from "./ToolEditorFormTest/ToolEditorFormTest";
import { ToolEditorTimeline } from "./ToolEditorTimeline/ToolEditorTimeline";
import { ToolEditorBottomBar } from "./BottomBar/ToolEditorBottomBar";
import {
  ToolEditorPublish,
  ToolEditorPublishValues,
} from "./ToolEditorPublish/ToolEditorPublish";
import { Formik } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";
import { useTenantContext } from "providers/TenantProvider/TenantProvider";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { useToolDataContext } from "pages/tool/ToolDataProvider";
import * as Yup from "yup";
import { useIdeasSearchParams } from "hooks/useIdeasSearchParams";
import { ToolEditorPageKey } from "../Provider/store/ToolEditorProvider.store";

const validationSchema = Yup.object({
  version: Yup.string().required("Please select a version"),
  changelog: Yup.string(),
});

export const ToolEditorForm = () => {
  const { getParam } = useIdeasSearchParams();
  const draftSpec = useToolEditorContext((s) => s.draftSpec);
  const testTable = useToolEditorContext((s) => s.testTable);
  const isContainerImageWarning = useToolEditorContext(
    (s) => s.isContainerImageWarning,
  );

  const page = getParam("TOOL_EDITOR_PAGE");

  const routeToPage = useToolEditorContext((s) => s.routeToPage);

  /**
   * Re route search params to match
   */
  useEffect(() => {
    switch (page) {
      case "code":
        break;
      case "config":
        if (draftSpec === null) {
          routeToPage("CODE");
        }

        break;
      case "test":
        if (testTable === null) {
          routeToPage("CODE");
        }
        break;
      case "publish":
        if (testTable === null) {
          routeToPage("CODE");
        }
        break;
      default:
        routeToPage("CODE");
        break;
    }
  }, [draftSpec, page, routeToPage, testTable]);

  const formPage: ToolEditorPageKey = useMemo(() => {
    switch (page) {
      case "code":
        return "CODE";
      case "config":
        return "SPEC";
      case "test":
        return "TEST";
      case "publish":
        return "PUBLISH";
      default:
        return "CODE";
    }
  }, [page]);

  const publishOptions = useToolEditorContext((s) => s.publishOptions);
  const tenantKey = useTenantContext((s) => s.currentTenant.key);
  const toolId = useToolEditorContext((s) => s.tool.id);
  const { invalidateToolQuery } = useToolDataContext();
  const navigateToToolPath = useRouteMapContext(
    (s) => s.routeMap["TOOL"].dynamicPath,
  );

  const publishVersion = useToolEditorContext((s) => s.publishVersion);

  const initialValues: ToolEditorPublishValues = useMemo(
    () => ({
      version: "first_version" in publishOptions ? "first_version" : "patch",
      docs: "",
      changelog: "",
    }),
    [publishOptions],
  );

  const handleSubmit = useCallback(
    async (values: ToolEditorPublishValues) => {
      try {
        await publishVersion(values.version, values.changelog);
        await invalidateToolQuery();

        addUtilityToastSuccess("Tool version published");
        navigateToToolPath({
          tenantKey,
          toolId: toolId,
        }).navigateTo();
      } catch (error) {
        addUtilityToastFailure("Failed to publish tool version");
      }
    },
    [
      invalidateToolQuery,
      navigateToToolPath,
      publishVersion,
      tenantKey,
      toolId,
    ],
  );

  return (
    <EuiFlexGroup
      direction="column"
      style={{
        marginLeft: "auto",
        marginRight: "auto",
        width: "100%",
        minWidth: 800,
        height: "100%",
      }}
      gutterSize="none"
      responsive={false}
    >
      <Formik<ToolEditorPublishValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnMount={false}
      >
        <>
          {isContainerImageWarning && (
            <EuiFlexItem grow={false}>
              <EuiCallOut
                iconType="package"
                color="danger"
                title="Missing Container Image"
              >
                The container image specified in the code bundle associated with
                this draft cannot be found - it may be deleted or is no longer
                accessible. You will not be able to execute test tasks or
                publish this draft. To fix this, upload a new code bundle with a
                valid container image in the{" "}
                <EuiLink onClick={() => routeToPage("CODE")}>Upload</EuiLink>{" "}
                step.
              </EuiCallOut>
            </EuiFlexItem>
          )}
          <EuiFlexItem grow={false}>
            <ToolEditorTimeline page={formPage} />
          </EuiFlexItem>
          <EuiSpacer size="m" />
          <EuiFlexItem grow>
            {(() => {
              switch (formPage) {
                case "CODE":
                  return <ToolEditorFormCode />;
                case "SPEC":
                  return <ToolEditorSpecEditor />;
                case "TEST":
                  return <ToolEditorFormTest />;
                case "PUBLISH":
                  return <ToolEditorPublish />;
              }
            })()}
          </EuiFlexItem>
          <ToolEditorBottomBar page={formPage} />
        </>
      </Formik>
    </EuiFlexGroup>
  );
};
